import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import { orderApi, customerApi } from '../../services/api'
import Button from '../../components/common/Button'

const MONTHS = [
    { value: '', label: 'All months' },
    { value: '1', label: 'January' },
    { value: '2', label: 'February' },
    { value: '3', label: 'March' },
    { value: '4', label: 'April' },
    { value: '5', label: 'May' },
    { value: '6', label: 'June' },
    { value: '7', label: 'July' },
    { value: '8', label: 'August' },
    { value: '9', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
]

export default function AdminOrders() {
    const currentYear = new Date().getFullYear()
    const years = [...Array(5)].map((_, i) => currentYear - i)

    const [orders, setOrders] = useState([])
    const [customers, setCustomers] = useState([])
    const [loading, setLoading] = useState(true)

    const [customerId, setCustomerId] = useState('')   // empty = all customers
    const [year, setYear] = useState('')
    const [month, setMonth] = useState('')
    const [statusFilter, setStatusFilter] = useState('')

    useEffect(() => { loadCustomers() }, [])
    useEffect(() => { load() }, [customerId, year, month])

    async function loadCustomers() {
        try {
            const response = await customerApi.list()
            if (response.success) setCustomers(response.customers)
        } catch { /* non-fatal */ }
    }

    async function load() {
        setLoading(true)
        try {
            const response = await orderApi.listAll(
                customerId || null,
                year || null,
                month || null
            )
            if (response.success) setOrders(response.orders)
            else toast.error(response.message || 'Could not load orders')
        } catch {
            toast.error('Network error')
        } finally {
            setLoading(false)
        }
    }

    async function handlePreviewPdf(orderId) {
        try {
            const { blob } = await orderApi.fetchPdf(orderId, { inline: true })
            const url = URL.createObjectURL(blob)
            window.open(url, '_blank')
            setTimeout(() => URL.revokeObjectURL(url), 60_000)
        } catch {
            toast.error('Could not load PDF')
        }
    }

    async function handleDownloadPdf(orderId) {
        try {
            const { blob, fileName } = await orderApi.fetchPdf(orderId, { inline: false })
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = fileName
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            URL.revokeObjectURL(url)
        } catch {
            toast.error('Could not download PDF')
        }
    }

    // Client-side status filter (cheap, runs over the fetched set)
    const filteredOrders = statusFilter
        ? orders.filter(o => o.status === statusFilter)
        : orders

    // Summary stats for the filtered set
    const stats = filteredOrders.reduce((acc, o) => {
        acc.total += Number(o.subtotal)
        acc.byStatus[o.status] = (acc.byStatus[o.status] || 0) + 1
        return acc
    }, { total: 0, byStatus: {} })

    return (
        <div className="space-y-4">
            <div>
                <h2 className="text-2xl font-semibold text-ghasc-navy">All Orders</h2>
                <p className="text-sm text-gray-500 mt-1">
                    Orders from all homegroups and subcommittees. Use filters to narrow the list.
                </p>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-xl shadow-sm p-4 flex flex-wrap items-end gap-3">
                <div className="min-w-[180px]">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Homegroup</label>
                    <select
                        value={customerId}
                        onChange={(e) => setCustomerId(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-ghasc-navy"
                    >
                        <option value="">All</option>
                        {customers.map(c => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                    <select
                        value={year}
                        onChange={(e) => setYear(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-ghasc-navy"
                    >
                        <option value="">All</option>
                        {years.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
                    <select
                        value={month}
                        onChange={(e) => setMonth(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-ghasc-navy"
                    >
                        {MONTHS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-ghasc-navy"
                    >
                        <option value="">All</option>
                        <option value="pending_payment">Pending Payment</option>
                        <option value="paid">Paid</option>
                        <option value="packed">Packed</option>
                        <option value="picked_up">Picked Up</option>
                        <option value="cancelled">Cancelled</option>
                    </select>
                </div>
                {(customerId || year || month || statusFilter) && (
                    <button
                        onClick={() => { setCustomerId(''); setYear(''); setMonth(''); setStatusFilter('') }}
                        className="text-sm text-ghasc-navy hover:underline pb-2"
                    >
                        Clear filters
                    </button>
                )}
            </div>

            {/* Summary bar — quick totals across the filtered set */}
            {!loading && filteredOrders.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm p-4 grid sm:grid-cols-4 gap-4 text-sm">
                    <div>
                        <div className="text-xs uppercase tracking-wider text-gray-500">Orders</div>
                        <div className="font-semibold text-lg text-ghasc-navy">{filteredOrders.length}</div>
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-wider text-gray-500">Pending Payment</div>
                        <div className="font-semibold text-lg text-amber-700">
                            {stats.byStatus.pending_payment || 0}
                        </div>
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-wider text-gray-500">Ready to Pack</div>
                        <div className="font-semibold text-lg text-blue-700">
                            {stats.byStatus.paid || 0}
                        </div>
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-wider text-gray-500">Total Value</div>
                        <div className="font-semibold text-lg text-ghasc-navy">
                            ${stats.total.toFixed(2)}
                        </div>
                    </div>
                </div>
            )}

            {/* Orders cards */}
            {loading ? (
                <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-500">Loading...</div>
            ) : filteredOrders.length === 0 ? (
                <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-500">
                    No orders match those filters.
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {filteredOrders.map(o => (
                        <div key={o.id} className="bg-white rounded-xl shadow-sm p-4 flex flex-col gap-3">
                            {/* Top row: order id + status */}
                            <div className="flex items-start justify-between gap-2">
                                <div>
                                    <Link to={`/orders/${o.id}`} className="text-ghasc-navy font-semibold hover:underline">
                                        Order #{o.id}
                                    </Link>
                                    <div className="text-xs text-gray-500 mt-0.5">
                                        {new Date(o.createdAt).toLocaleDateString('en-US', {
                                            month: 'short', day: 'numeric', year: 'numeric',
                                        })}
                                    </div>
                                </div>
                                <StatusBadge status={o.status} />
                            </div>

                            {/* Homegroup + submitter */}
                            <div>
                                <div className="text-sm font-medium text-gray-900">{o.customerName}</div>
                                <div className="text-xs text-gray-500">
                                    {o.submitterName} Â· {cycleToDisplay(o.cycleLabel)}
                                </div>
                            </div>

                            {/* Counts + total */}
                            <div className="grid grid-cols-2 gap-2 text-sm">
                                <div>
                                    <div className="text-xs uppercase tracking-wider text-gray-500">Items</div>
                                    <div className="font-medium">
                                        {o.lineCount}
                                        {o.backorderCount > 0 && (
                                            <span className="ml-1 text-xs text-amber-700">
                                                ({o.backorderCount} BO)
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="text-xs uppercase tracking-wider text-gray-500">Total</div>
                                    <div className="font-semibold text-ghasc-navy">${Number(o.subtotal).toFixed(2)}</div>
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="flex gap-2 pt-2 border-t border-gray-100">
                                <button
                                    onClick={() => handlePreviewPdf(o.id)}
                                    className="flex-1 px-3 py-2 text-sm text-ghasc-navy bg-gray-50 rounded-lg hover:bg-gray-100"
                                >
                                    Preview
                                </button>
                                <button
                                    onClick={() => handleDownloadPdf(o.id)}
                                    className="flex-1 px-3 py-2 text-sm text-ghasc-navy bg-gray-50 rounded-lg hover:bg-gray-100"
                                >
                                    Download
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <div className="text-xs text-gray-500">
                Showing {filteredOrders.length} order{filteredOrders.length === 1 ? '' : 's'}
            </div>
        </div>
    )
}

// ---------------------------------------------------------------------------
//  Helpers
// ---------------------------------------------------------------------------
function StatusBadge({ status }) {
    const map = {
        pending_payment: { label: 'Pending Payment', cls: 'bg-amber-100 text-amber-800' },
        paid: { label: 'Paid', cls: 'bg-blue-100 text-blue-800' },
        packed: { label: 'Packed', cls: 'bg-indigo-100 text-indigo-800' },
        picked_up: { label: 'Picked Up', cls: 'bg-green-100 text-green-800' },
        cancelled: { label: 'Cancelled', cls: 'bg-gray-100 text-gray-600' },
    }
    const m = map[status] || { label: status, cls: 'bg-gray-100 text-gray-700' }
    return (
        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${m.cls}`}>
            {m.label}
        </span>
    )
}

function cycleToDisplay(label) {
    if (!label || label.length !== 7) return label || ''
    const year = parseInt(label.slice(0, 4), 10)
    const month = parseInt(label.slice(5), 10)
    if (isNaN(year) || isNaN(month)) return label
    const months = ['January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December']
    return `${months[month - 1]} ${year}`
}